"use client";
import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { API_BASE_URL } from '@/app/lib/api';

// On importe le composant ProductCard et son type
import ProductCard, { Product as ProductCardType } from "../produit/product_card";

// Swiper imports
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

type Category = {
  id?: string;
  name?: string;
  slug?: string;
  image?: string | null;
  products: any[]; // On va mapper cela vers ProductCardType
  colorHome?: string | null;
};

type Props = {
  specialSlugs?: string[];
  limit?: number;
  orderBy?: "createdAt" | "updatedAt" | "id";
  direction?: "asc" | "desc";
};

export default function TopSpecialsSection({
  specialSlugs = ["meilleures-ventes", "nos-coups-de-coeur"],
  limit = 10,
  orderBy = "createdAt",
  direction = "desc",
}: Props) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const EFFECT_API_BASE = (API_BASE_URL ?? "").replace(/\/$/, "");

  useEffect(() => {
    let active = true;

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const endpoint = `${EFFECT_API_BASE}/categories/top-products-by-special-slug?limit=${limit}&orderBy=${orderBy}&direction=${direction}`;
        const res = await fetch(endpoint);

        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

        const data = await res.json();
        const arr = Array.isArray(data) ? data : [];

        if (!active) return;

        const desiredSlugs = Array.isArray(specialSlugs) ? specialSlugs : [];
        const filteredCategories = desiredSlugs.length > 0
          ? arr.filter((c: any) => c.slug && desiredSlugs.includes(c.slug))
          : arr;

        setCategories(filteredCategories);
      } catch (err: any) {
        if (active) {
          setError(err?.message ?? "Erreur de chargement");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => {
      active = false;
    };
  }, [EFFECT_API_BASE, limit, orderBy, direction, specialSlugs]);

  function Carousel({ cat }: { cat: Category }) {
    // Determine a safe loop condition: loop only when we have more items than the largest slidesPerView
    const MAX_SLIDES_PER_VIEW = 5.5;
    const shouldLoop = cat.products.length > MAX_SLIDES_PER_VIEW;
    const navSuffix = String(cat.slug ?? cat.id).replace(/[^a-z0-9-_]/gi, '').slice(0, 40);

    return (
      <section className="container max-w-6xl mx-auto pb-12 relative overflow-visible">
        <div className="text-left mb-6">
          <div className="flex items-center gap-3">
            <h3 className="text-3xl md:text-4xl lg:text-6xl font-regular text-primary">{cat.name}</h3>
          </div>
          {cat.slug === "nos-coups-de-coeur" && (
            <p className="mt-2 text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-400 capitalize font-light">
              nos indispensables, toujours au même prix
            </p>
          )}
        </div>

        {/* Navigation buttons */}
        <button className={`hidden md:flex absolute left-0 md:-left-12 top-1/2 -translate-y-1/2 z-30 w-10 h-10 items-center justify-center bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors prev-${navSuffix}`}>
          <ChevronLeft size={24} color="#343434" />
        </button>
        <button className={`hidden md:flex absolute right-0 md:-right-12 top-1/2 -translate-y-1/2 z-30 w-10 h-10 items-center justify-center bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors next-${navSuffix}`}>
          <ChevronRight size={24} color="#343434" />
        </button>

        <Swiper
          modules={[Navigation, Autoplay]}
          navigation={{ prevEl: `.prev-${navSuffix}`, nextEl: `.next-${navSuffix}` }}
          loop={shouldLoop}
          autoplay={{ delay: 4000, disableOnInteraction: false, pauseOnMouseEnter: true }}
          spaceBetween={16}
          slidesPerView={1.5}
          breakpoints={{
            480: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
            1536: { slidesPerView: 4 },
          }}
          className="overflow-visible"
        >
          {cat.products.map((p, idx) => {
            const productForCard: ProductCardType = {
              id: String(p.id),
              name: p.name ?? p.title ?? "Produit sans titre",
              slug: p.slug ?? String(p.id),
              price: Number(p.price),
              promoPrice: p.promoPrice ? Number(p.promoPrice) : null,
              images: Array.isArray(p.images)
                ? p.images.map((img: any) => typeof img === 'string' ? img : img.url)
                : ["/logo.png.webp"],
              stock: p.stock ?? 0,
              category: {
                id: cat.id ?? "",
                name: cat.name ?? ""
              },
              childCategoryName: p.category?.name ?? p.childCategoryName ?? null
            };

            return (
              <SwiperSlide key={`${productForCard.id}-${idx}`} className="h-auto">
                <ProductCard
                  product={productForCard}
                  className="h-full shadow-sm rounded-lg"
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </section>
    );
  }

  return (
    <div className="py-12">
      <div className="container max-w-screen-2xl mx-auto px-4 text-primary">
        {loading ? (
          <div className="py-20 text-center text-lg text-gray-500 animate-pulse">Chargement des meilleures offres...</div>
        ) : error ? (
          <div className="py-20 text-center text-red-500">{error}</div>
        ) : (
          categories.map((c) => <Carousel key={c.id ?? c.slug} cat={c} />)
        )}
      </div>
    </div>
  );
}